'use client'

import { Star, Quote } from 'lucide-react'
import { useT } from '@/lib/useT'

const testimonialsConfig = [
  {
    nameKey: 'testimonials.name2',
    roleKey: 'testimonials.title2',
    companyKey: 'testimonials.company2',
    quoteKey: 'testimonials.quote2',
    initials: 'KR',
    color: '#003F47',
  },
  {
    nameKey: 'testimonials.name1',
    roleKey: 'testimonials.title1',
    companyKey: 'testimonials.company1',
    quoteKey: 'testimonials.quote1',
    initials: 'LS',
    color: '#1A535A',
  },
  {
    nameKey: 'testimonials.name4',
    roleKey: 'testimonials.title4',
    companyKey: 'testimonials.company4',
    quoteKey: 'testimonials.quote4',
    initials: 'OH',
    color: '#22505A',
  },
  {
    nameKey: 'testimonials.name3',
    roleKey: 'testimonials.title3',
    companyKey: 'testimonials.company3',
    quoteKey: 'testimonials.quote3',
    initials: 'RF',
    color: '#22614A',
  },
]

export default function Testimonials() {
  const t = useT()
  
  return (
    <section
      id="testimonials"
      className="py-24 bg-[#FFF9F2]"
      aria-labelledby="testimonials-heading"
    >
      <div className="mx-auto max-w-[1200px] px-6">
        {/* Header */}
        <div className="flex flex-col items-center text-center gap-4 mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-[999px] bg-[#003F47]/8 text-[#003F47]">
            <Star size={14} className="fill-[#003F47]" aria-hidden="true" />
            <span className="text-xs font-semibold uppercase tracking-[0.12em]">{t('testimonials.badge')}</span>
          </div>
          <h2
            id="testimonials-heading"
            className="text-3xl md:text-4xl font-bold text-[#0A171D] tracking-[-0.02em] leading-[1.08] text-balance"
          >
            {t('testimonials.heading')}
          </h2>
          <p className="text-base text-[#6C7377] leading-relaxed max-w-xl text-pretty">
            {t('testimonials.subheading')}
          </p>
        </div>

        <ul className="grid grid-cols-1 md:grid-cols-2 gap-6" role="list">
          {testimonialsConfig.map((item) => (
            <li
              key={item.nameKey}
              className="card-lift group flex flex-col gap-5 p-8 bg-white rounded-[18px] border border-[#E2E4E5] shadow-[0_1px_2px_rgba(10,23,29,0.06),0_2px_6px_rgba(10,23,29,0.06)]"
            >
              {/* Top row: stars + quote icon */}
              <div className="flex items-center justify-between">
                <div className="flex gap-1" aria-label="5 out of 5 stars">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      size={15}
                      className="fill-[#FFBD76] text-[#FFBD76]"
                      aria-hidden="true"
                    />
                  ))}
                </div>
                <Quote
                  size={28}
                  className="text-[#E2E4E5] group-hover:text-[#C2D2D5] transition-colors duration-[220ms]"
                  aria-hidden="true"
                />
              </div>

              {/* Quote */}
              <blockquote className="flex-1 text-base text-[#16242B] leading-relaxed">
                &ldquo;{t(item.quoteKey as any)}&rdquo;
              </blockquote>

              {/* Divider */}
              <div className="h-px bg-[#E2E4E5]" aria-hidden="true" />

              {/* Author */}
              <div className="flex items-center gap-3">
                <div
                  className="flex h-10 w-10 items-center justify-center rounded-full text-[#FFFBF7] text-sm font-bold shrink-0"
                  style={{ backgroundColor: item.color }}
                  aria-hidden="true"
                >
                  {item.initials}
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#0A171D] leading-snug">{t(item.nameKey as any)}</p>
                  <p className="text-xs text-[#6C7377]">
                    {t(item.roleKey as any)}, <span className="text-[#003F47]">{t(item.companyKey as any)}</span>
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ul>

        {/* Social proof strip */}
        <div className="mt-12 flex flex-wrap items-center justify-center gap-8">
          {[
            { val: '4.9', label: 'G2 rating', sub: '(320+ reviews)' },
            { val: '4.8', label: 'Capterra', sub: '(180+ reviews)' },
            { val: '#1', label: 'MENA ERP', sub: `(2024 ${t('testimonials.categoryLeader')})` },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-3">
              <div className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={12} className="fill-[#FFBD76] text-[#FFBD76]" aria-hidden="true" />
                ))}
              </div>
              <span className="text-sm font-bold text-[#0A171D]">{item.val}</span>
              <span className="text-sm text-[#6C7377]">{item.label}</span>
              <span className="text-xs text-[#9EA2A5]">{item.sub}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
